import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { TimerIcon, MapPinIcon, TargetIcon } from 'lucide-react';
export function SolutionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const mechanisms = [{
    icon: TimerIcon,
    title: 'Timer',
    description: '15 minutos. Tú mandas. Cuando suena, paras.'
  }, {
    icon: MapPinIcon,
    title: 'Zonas',
    description: 'Una sola zona por sesión (cocina, baño, sala/entrada, dormitorio, ropa).'
  }, {
    icon: TargetIcon,
    title: 'Misiones',
    description: 'Una tarea concreta que produce un cambio visible (no "limpiar", sino "encimera + fregadero").'
  }];
  return <section ref={ref} className="w-full bg-[#7B9FAB] py-20 md:py-32 px-4" data-id="element-190">
      <div className="max-w-6xl mx-auto" data-id="element-191">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-192">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-193">
            Presentando: Reset de 15 Minutos (Anti-Caos)
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto" data-id="element-194">
            Un sistema simple para recuperar control sin convertir tu noche en
            una limpieza eterna.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {
        opacity: 0
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 md:p-12 mb-12" data-id="element-195">
          <h3 className="text-2xl md:text-3xl font-serif text-[#3A3A3A] mb-8 text-center" data-id="element-196">
            El mecanismo (lo único que tienes que recordar)
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12" data-id="element-197">
            {mechanisms.map((mechanism, index) => <motion.div key={mechanism.title} initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} transition={{
            duration: 0.6,
            delay: 0.4 + index * 0.1
          }} className="text-center space-y-4" data-id="element-198">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#7B9FAB]/10" data-id="element-199">
                  <mechanism.icon className="w-8 h-8 text-[#7B9FAB]" aria-hidden="true" data-id="element-200" />
                </div>
                <h4 className="text-2xl font-serif text-[#3A3A3A]" data-id="element-201">
                  {mechanism.title}
                </h4>
                <p className="text-[#5A5A5A] leading-relaxed" data-id="element-202">
                  {mechanism.description}
                </p>
              </motion.div>)}
          </div>

          <div className="mt-12 pt-8 border-t border-[#7B9FAB]/20" data-id="element-203">
            <p className="text-lg text-[#5A5A5A] text-center leading-relaxed" data-id="element-204">
              Esto evita el sabotaje típico: empiezas "un poquito" y terminas
              atrapada 2 horas.
            </p>
            <p className="text-xl font-medium text-[#3A3A3A] text-center mt-4" data-id="element-205">
              Aquí no. Aquí ganas en 15 minutos.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="text-center text-white space-y-6" data-id="element-206">
          <h3 className="text-2xl md:text-3xl font-serif mb-4" data-id="element-207">
            "Pero… ¿15 minutos realmente sirven?"
          </h3>
          <p className="text-lg text-white/90 max-w-3xl mx-auto leading-relaxed" data-id="element-208">
            Sí, porque el objetivo no es "terminar". El objetivo es{' '}
            <strong data-id="element-209">reducir el caos visible</strong> que te está drenando.
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 max-w-2xl mx-auto mt-8" data-id="element-210">
            <p className="text-white/90 mb-4" data-id="element-211">
              Cuando el caos baja, pasan 3 cosas:
            </p>
            <ol className="text-left space-y-3 text-white/90" data-id="element-212">
              <li data-id="element-213">1) respiras distinto</li>
              <li data-id="element-214">2) descansas mejor</li>
              <li data-id="element-215">3) el siguiente reset cuesta menos</li>
            </ol>
          </div>
          <p className="text-lg text-white/90 italic" data-id="element-216">
            No es magia. Es acumulación. 15 minutos al día por 7 días = una casa
            que deja de sentirse como un enemigo.
          </p>
        </motion.div>
      </div>
    </section>;
}